import { useRef, useState } from 'react';
import { Rive } from '@rive-app/react-canvas';

import { usePausePlaySettings } from 'approot/shared/play-pause/play-pause.hooks';

// Playback state for one render site. RiveView shows the same component through
// two independent RiveAnimations — the inline one and the expand modal's — and
// each has its own `useRive` instance that auto-play being off leaves paused on
// mount, so neither the started state nor the play/pause state can be shared:
// starting one site must not dismiss the other site's overlay over a frame that
// is still frozen, nor leave its kebab offering Pause for an animation that
// isn't running.
const useRiveViewPlayback = ({
  riveKey,
  includeStartOverlay,
}: {
  riveKey: string | null;
  includeStartOverlay: boolean;
}) => {
  const { isPlaying, shouldAutoPlay, setIsPlaying } = usePausePlaySettings();
  // Which .riv file this site has started, rather than a plain boolean. A live
  // riveKey change remounts RiveAnimation on a fresh paused instance while this
  // state's owner stays mounted, so keying on the file leaves the new one
  // awaiting its own start instead of inheriting the previous file's spent flag.
  // `undefined` is the never-started value because a component with no file has
  // a null key.
  const [startedRiveKey, setStartedRiveKey] = useState<
    string | null | undefined
  >();
  // The one gate behind the covered state: the Start overlay shows and
  // RiveAnimation suppresses the interactivity sitting under it. Keyed on the
  // started file, not isPlaying, so it never returns once the animation has run.
  const isAwaitingStart =
    includeStartOverlay && !shouldAutoPlay && startedRiveKey !== riveKey;

  // Shared by the overlay's Start button and the kebab's Play/Pause item, so
  // Start is never a parallel/duplicate implementation of the kebab's existing
  // toggle — just another caller of the same one.
  const handlePlayPauseToggle = (rive: Rive | null) => {
    // `useRive` resolves its instance asynchronously (null until the .riv
    // file's Load event) while both the overlay and the kebab's Play/Pause item
    // render from first paint, so either can be clicked before there is
    // anything to play. Bail instead of falling through: the state would flip
    // on a no-op play()/pause(), leaving the UI asserting a state the animation
    // isn't in — and with auto-play off it would also consume the overlay,
    // after which `RiveAnimation`'s autoplay-enforcement effect (keyed on the
    // null→instance transition) pauses the freshly-loaded file for good.
    if (!rive) {
      return;
    }
    // isAwaitingStart means this file has never run, whatever isPlaying says:
    // a live riveKey swap leaves isPlaying true from the previous file, and
    // starting the new one must not be read as a pause.
    const shouldPlay = isAwaitingStart || !isPlaying;
    if (shouldPlay) {
      // Retiring the overlay from the start branch rather than from
      // handleStartClick means starting from the kebab retires it too, so the
      // overlay can never end up covering a playing animation — while a pause
      // leaves the state alone, since pausing is not starting.
      setStartedRiveKey(riveKey);
      rive.play();
    } else {
      rive.pause();
    }
    setIsPlaying(shouldPlay);
  };

  // Start only renders while isAwaitingStart, so this handler only ever runs
  // for a transition the user just caused by clicking it — unlike an effect
  // keyed on isAwaitingStart, which would also fire when the class's
  // "Automatic animation playback" setting changes live, stealing focus into
  // every visible Rive component that stops awaiting a start at that moment.
  // The view wrapper is mounted regardless of isAwaitingStart (tabIndex={-1},
  // unconditional), so focusing it here — before the overlay button unmounts
  // — lands focus deliberately instead of dropping it to document.body.
  const viewRef = useRef<HTMLDivElement>(null);
  const handleStartClick = (rive: Rive | null) => {
    // Repeated here on top of the toggle's own not-yet-loaded bail (explained
    // there) because a click landing before the file loads starts nothing, so
    // it must not pull focus either.
    if (!rive) {
      return;
    }
    handlePlayPauseToggle(rive);
    viewRef.current?.focus();
  };

  return {
    isPlaying,
    isAwaitingStart,
    shouldAutoPlay,
    viewRef,
    handlePlayPauseToggle,
    handleStartClick,
  };
};

export default useRiveViewPlayback;
